/**
 * Export a composition to an MP4.
 *
 * Primary path — WebCodecs + mp4-muxer, frame by frame:
 *   every frame seeks each visible video to its exact source time, waits for
 *   the decoded frame, draws it with the same `renderFrame` the preview uses
 *   and hands the canvas to a `VideoEncoder` (H.264). Audio is mixed offline
 *   (`OfflineAudioContext`: clip sound + music, volumes, fades, transition
 *   crossfades) and encoded with an `AudioEncoder` (AAC) into the same file.
 *   It is deterministic and runs faster or slower than real time as the
 *   machine allows; nothing depends on the tab keeping up.
 *
 * Fallback — browsers without WebCodecs: play the composition in real time on
 * a canvas and record it with MediaRecorder (+ the mixed audio).
 */
import { ArrayBufferTarget, Muxer } from 'mp4-muxer';

import {
    VideoComposition,
    audioTracks,
    clamp01,
    clipSpeed,
    frameCount,
    itemEnd,
    mainFrameState,
    mainTrack,
    normalizeComposition,
    overlayTracks,
    sourceTimeAt,
    totalDuration,
} from './composition';
import { ensureFonts, FontUse } from './fonts';
import { MediaCache } from './media';
import { PlaybackEngine } from './player';
import { invalidateTextLayouts, renderFrame } from './render';

export type ExportPhase = 'prepare' | 'audio' | 'video' | 'finalize';

export interface ExportProgress {
    phase: ExportPhase;
    /** 0..1 over the whole export. */
    progress: number;
    frame?: number;
    frames?: number;
}

export interface ExportOptions {
    onProgress?: (p: ExportProgress) => void;
    signal?: AbortSignal;
    /** Bits per second for the picture (default scales with resolution, ~10 Mbps at 1080×1920). */
    videoBitrate?: number;
    /** Force the real-time MediaRecorder path (testing). */
    forceRealtime?: boolean;
}

export interface ExportResult {
    blob: Blob;
    mimeType: string;
    extension: 'mp4' | 'webm';
    width: number;
    height: number;
    durationSec: number;
    /** A PNG of the poster frame. */
    thumbnail: Blob | null;
    /** Which path produced the file. */
    method: 'webcodecs' | 'mediarecorder';
    hasAudio: boolean;
}

const SAMPLE_RATE = 48000;

export class ExportCancelledError extends Error {
    constructor() {
        super('Export cancelled');
        this.name = 'AbortError';
    }
}

function checkAbort(signal?: AbortSignal) {
    if (signal?.aborted) throw new ExportCancelledError();
}

export function hasWebCodecs(): boolean {
    const g = globalThis as any;
    return typeof g.VideoEncoder === 'function' && typeof g.VideoFrame === 'function';
}

export function fontsUsed(comp: VideoComposition): FontUse[] {
    const uses: FontUse[] = [];
    for (const t of overlayTracks(comp)) {
        for (const l of t.layers) {
            if (l.kind === 'text') uses.push({ family: l.fontFamily, weight: l.fontWeight, style: l.fontStyle });
        }
    }
    return uses;
}

/** The frame to use as the poster: past any opening fade. */
export function posterTime(comp: VideoComposition): number {
    const first = mainTrack(comp).clips[0];
    const fade = first?.transitionIn?.duration ?? 0;
    return Math.min(totalDuration(comp) - 1 / comp.fps, Math.max(0, fade + 0.35));
}

// ─── audio ────────────────────────────────────────────────────────────

async function fetchDecoded(ctx: BaseAudioContext, src: string, signal?: AbortSignal): Promise<AudioBuffer | null> {
    try {
        const res = await fetch(src, { mode: 'cors', signal });
        if (!res.ok) return null;
        const buf = await res.arrayBuffer();
        return await new Promise<AudioBuffer | null>((resolve) => {
            // The callback form, for Safari; a video with no sound rejects.
            const p = ctx.decodeAudioData(buf, (b) => resolve(b), () => resolve(null));
            if (p && typeof (p as any).catch === 'function') (p as Promise<AudioBuffer>).catch(() => resolve(null));
        });
    } catch (err: any) {
        if (err?.name === 'AbortError') throw new ExportCancelledError();
        return null;
    }
}

/**
 * Mix every audible source into one stereo buffer the length of the video.
 * Returns null when nothing makes a sound.
 */
export async function mixAudio(comp: VideoComposition, signal?: AbortSignal): Promise<AudioBuffer | null> {
    const duration = totalDuration(comp);
    if (duration <= 0 || typeof (globalThis as any).OfflineAudioContext !== 'function') return null;
    const clips = mainTrack(comp).clips;
    const sources: Array<{ src: string; start: number; offset: number; duration: number; volume: number; fadeIn: number; fadeOut: number; rate: number }> = [];
    clips.forEach((c, i) => {
        if (c.kind !== 'video' || c.muted || c.volume <= 0) return;
        const next = clips[i + 1];
        sources.push({
            src: c.src,
            start: c.start,
            offset: c.trimIn,
            duration: c.duration,
            volume: c.volume,
            // Sound crossfades with the picture.
            fadeIn: i > 0 && c.transitionIn ? c.transitionIn.duration : 0.02,
            fadeOut: next?.transitionIn ? next.transitionIn.duration : 0.02,
            rate: clipSpeed(c),
        });
    });
    for (const track of audioTracks(comp)) {
        if (track.muted) continue;
        for (const a of track.clips) {
            if (a.volume <= 0) continue;
            sources.push({ src: a.src, start: a.start, offset: a.trimIn, duration: a.duration, volume: a.volume, fadeIn: a.fadeIn, fadeOut: a.fadeOut, rate: 1 });
        }
    }
    if (!sources.length) return null;
    const length = Math.ceil(duration * SAMPLE_RATE);
    const ctx = new OfflineAudioContext(2, length, SAMPLE_RATE);
    const decoded = new Map<string, AudioBuffer | null>();
    for (const src of Array.from(new Set(sources.map((s) => s.src)))) {
        checkAbort(signal);
        decoded.set(src, await fetchDecoded(ctx, src, signal));
    }
    let any = false;
    for (const s of sources) {
        const buffer = decoded.get(s.src);
        if (!buffer) continue;
        // Timeline seconds the source can fill: a slowed clip stretches its buffer.
        const playable = Math.min(s.duration, Math.max(0, buffer.duration - s.offset) / s.rate, duration - s.start);
        if (playable <= 0.01) continue;
        any = true;
        const node = ctx.createBufferSource();
        node.buffer = buffer;
        const gain = ctx.createGain();
        const g = gain.gain;
        const t0 = s.start;
        const t1 = s.start + playable;
        const fi = Math.min(s.fadeIn, playable / 2);
        const fo = Math.min(s.fadeOut, playable / 2);
        g.setValueAtTime(fi > 0 ? 0 : s.volume, t0);
        if (fi > 0) g.linearRampToValueAtTime(s.volume, t0 + fi);
        g.setValueAtTime(s.volume, Math.max(t0 + fi, t1 - fo));
        if (fo > 0) g.linearRampToValueAtTime(0, t1);
        node.connect(gain).connect(ctx.destination);
        if (s.rate !== 1) {
            node.playbackRate.value = s.rate;
            // start()'s duration is in buffer time; stop at the timeline edge instead.
            node.start(t0, s.offset);
            node.stop(t1);
        } else {
            node.start(t0, s.offset, playable);
        }
    }
    if (!any) return null;
    checkAbort(signal);
    return ctx.startRendering();
}

interface AudioPlan {
    codec: 'aac' | 'opus';
    config: any;
}

async function pickAudioCodec(): Promise<AudioPlan | null> {
    const AE = (globalThis as any).AudioEncoder;
    if (typeof AE !== 'function') return null;
    const candidates: AudioPlan[] = [
        { codec: 'aac', config: { codec: 'mp4a.40.2', sampleRate: SAMPLE_RATE, numberOfChannels: 2, bitrate: 192_000 } },
        { codec: 'opus', config: { codec: 'opus', sampleRate: SAMPLE_RATE, numberOfChannels: 2, bitrate: 160_000 } },
    ];
    for (const c of candidates) {
        try {
            const res = await AE.isConfigSupported(c.config);
            if (res?.supported) return c;
        } catch { /* try the next */ }
    }
    return null;
}

export async function pickVideoConfig(width: number, height: number, fps: number, bitrate: number): Promise<any | null> {
    const VE = (globalThis as any).VideoEncoder;
    const codecs = ['avc1.640028', 'avc1.4d0028', 'avc1.640032', 'avc1.42e028', 'avc1.42003e'];
    for (const codec of codecs) {
        for (const hardwareAcceleration of ['prefer-hardware', 'no-preference']) {
            const config = {
                codec,
                width,
                height,
                bitrate,
                framerate: fps,
                hardwareAcceleration,
                avc: { format: 'avc' },
                bitrateMode: 'variable',
                latencyMode: 'quality',
            };
            try {
                const res = await VE.isConfigSupported(config);
                if (res?.supported) return res.config || config;
            } catch { /* next */ }
        }
    }
    return null;
}

// ─── WebCodecs path ───────────────────────────────────────────────────

async function exportWebCodecs(comp: VideoComposition, opts: ExportOptions, media: MediaCache): Promise<ExportResult> {
    const { width, height, fps } = comp;
    const signal = opts.signal;
    const durationSec = totalDuration(comp);
    const frames = frameCount(comp);
    const report = (phase: ExportPhase, progress: number, frame?: number) =>
        opts.onProgress?.({ phase, progress: clamp01(progress), frame, frames });

    const bitrate = opts.videoBitrate ?? Math.round(10_000_000 * ((width * height) / (1080 * 1920)) ** 0.75);
    const videoConfig = await pickVideoConfig(width, height, fps, bitrate);
    if (!videoConfig) throw new Error('H.264 encoding is not supported in this browser');

    report('audio', 0.03);
    const audioBuffer = await mixAudio(comp, signal);
    const audioPlan = audioBuffer ? await pickAudioCodec() : null;
    checkAbort(signal);

    const target = new ArrayBufferTarget();
    const muxer = new Muxer({
        target,
        video: { codec: 'avc', width, height, frameRate: fps },
        audio: audioPlan ? { codec: audioPlan.codec, numberOfChannels: 2, sampleRate: SAMPLE_RATE } : undefined,
        fastStart: 'in-memory',
        firstTimestampBehavior: 'offset',
    } as any);

    let encodeError: any = null;
    const VE = (globalThis as any).VideoEncoder;
    const encoder = new VE({
        output: (chunk: any, meta: any) => muxer.addVideoChunk(chunk, meta),
        error: (e: any) => { encodeError = e; },
    });
    encoder.configure(videoConfig);

    // Audio first: it is quick, and the muxer interleaves either way.
    if (audioBuffer && audioPlan) {
        const AE = (globalThis as any).AudioEncoder;
        const AD = (globalThis as any).AudioData;
        const aenc = new AE({
            output: (chunk: any, meta: any) => muxer.addAudioChunk(chunk, meta),
            error: (e: any) => { encodeError = e; },
        });
        aenc.configure(audioPlan.config);
        const chunk = 1024 * 4;
        const total = audioBuffer.length;
        const left = audioBuffer.getChannelData(0);
        const right = audioBuffer.numberOfChannels > 1 ? audioBuffer.getChannelData(1) : left;
        for (let i = 0; i < total; i += chunk) {
            checkAbort(signal);
            const n = Math.min(chunk, total - i);
            const planar = new Float32Array(n * 2);
            planar.set(left.subarray(i, i + n), 0);
            planar.set(right.subarray(i, i + n), n);
            const data = new AD({
                format: 'f32-planar',
                sampleRate: SAMPLE_RATE,
                numberOfFrames: n,
                numberOfChannels: 2,
                timestamp: Math.round((i / SAMPLE_RATE) * 1e6),
                data: planar,
            });
            aenc.encode(data);
            data.close();
        }
        await aenc.flush();
        aenc.close();
    }
    report('video', 0.08);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', { alpha: false }) as CanvasRenderingContext2D;
    const VF = (globalThis as any).VideoFrame;
    const clips = mainTrack(comp).clips;
    const posterFrame = Math.min(frames - 1, Math.round(posterTime(comp) * fps));
    let thumbnail: Blob | null = null;
    const frameUs = 1e6 / fps;
    const gop = fps * 2;

    try {
        for (let i = 0; i < frames; i++) {
            checkAbort(signal);
            if (encodeError) throw encodeError;
            const t = i / fps;
            const state = mainFrameState(clips, t);
            const seeks: Promise<void>[] = [];
            for (const c of [state.current, state.previous]) {
                if (c && c.kind === 'video') seeks.push(media.seekVideo(c, sourceTimeAt(c, t) + 0.0005));
            }
            if (seeks.length) await Promise.all(seeks);
            renderFrame(ctx, comp, t, media);
            if (i === posterFrame) {
                thumbnail = await new Promise<Blob | null>((resolve) => canvas.toBlob((b) => resolve(b), 'image/png'));
            }
            const frame = new VF(canvas, { timestamp: Math.round(i * frameUs), duration: Math.round(frameUs) });
            encoder.encode(frame, { keyFrame: i % gop === 0 });
            frame.close();
            // Backpressure: never let the encoder queue run away with memory.
            while (encoder.encodeQueueSize > 6) {
                await new Promise((r) => setTimeout(r, 4));
            }
            if (i % 3 === 0) report('video', 0.08 + 0.88 * ((i + 1) / frames), i + 1);
        }
        await encoder.flush();
        if (encodeError) throw encodeError;
    } finally {
        try { encoder.close(); } catch { /* already closed */ }
    }
    report('finalize', 0.98, frames);
    muxer.finalize();
    const blob = new Blob([target.buffer], { type: 'video/mp4' });
    report('finalize', 1, frames);
    return {
        blob,
        mimeType: 'video/mp4',
        extension: 'mp4',
        width,
        height,
        durationSec,
        thumbnail,
        method: 'webcodecs',
        hasAudio: !!audioPlan,
    };
}

// ─── MediaRecorder fallback ───────────────────────────────────────────

function pickRecorderMime(): { mime: string; extension: 'mp4' | 'webm' } {
    const MR = (globalThis as any).MediaRecorder;
    const options: Array<{ mime: string; extension: 'mp4' | 'webm' }> = [
        { mime: 'video/mp4;codecs=avc1.640028,mp4a.40.2', extension: 'mp4' },
        { mime: 'video/mp4', extension: 'mp4' },
        { mime: 'video/webm;codecs=vp9,opus', extension: 'webm' },
        { mime: 'video/webm;codecs=vp8,opus', extension: 'webm' },
        { mime: 'video/webm', extension: 'webm' },
    ];
    for (const o of options) if (MR?.isTypeSupported?.(o.mime)) return o;
    return { mime: '', extension: 'webm' };
}

async function exportRealtime(comp: VideoComposition, opts: ExportOptions, media: MediaCache): Promise<ExportResult> {
    const MR = (globalThis as any).MediaRecorder;
    if (typeof MR !== 'function') throw new Error('This browser cannot record video');
    const signal = opts.signal;
    const { width, height, fps } = comp;
    const durationSec = totalDuration(comp);
    opts.onProgress?.({ phase: 'audio', progress: 0.02 });
    const audioBuffer = await mixAudio(comp, signal);
    checkAbort(signal);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    // Video sound is in the mixed buffer — the playing elements stay silent.
    const engine = new PlaybackEngine(canvas, media);
    const silent: VideoComposition = {
        ...comp,
        tracks: comp.tracks.filter((t) => t.kind !== 'audio'),
    };
    engine.setComposition(silent);
    media.allVideos().forEach((el) => { el.muted = true; });

    // Poster first, while everything is still.
    engine.seek(posterTime(comp));
    await Promise.all(mainTrack(comp).clips.filter((c) => c.kind === 'video').map((c) => {
        const at = posterTime(comp);
        return at >= c.start && at < itemEnd(c) ? media.seekVideo(c, sourceTimeAt(c, at)) : Promise.resolve();
    }));
    engine.draw();
    const thumbnail = await new Promise<Blob | null>((resolve) => canvas.toBlob((b) => resolve(b), 'image/png'));
    engine.seek(0);

    const stream: MediaStream = (canvas as any).captureStream(fps);
    let audioCtx: AudioContext | null = null;
    let audioNode: AudioBufferSourceNode | null = null;
    if (audioBuffer) {
        audioCtx = new AudioContext({ sampleRate: SAMPLE_RATE });
        const dest = audioCtx.createMediaStreamDestination();
        audioNode = audioCtx.createBufferSource();
        audioNode.buffer = audioBuffer;
        audioNode.connect(dest);
        dest.stream.getAudioTracks().forEach((tr) => stream.addTrack(tr));
    }
    const { mime, extension } = pickRecorderMime();
    const recorder = new MR(stream, {
        ...(mime ? { mimeType: mime } : {}),
        videoBitsPerSecond: opts.videoBitrate ?? 10_000_000,
    });
    const chunks: Blob[] = [];
    recorder.ondataavailable = (e: any) => { if (e.data?.size) chunks.push(e.data); };
    const stopped = new Promise<void>((resolve) => { recorder.onstop = () => resolve(); });

    await new Promise((r) => setTimeout(r, 300));
    recorder.start(250);
    audioNode?.start();
    engine.stopAtEnd = true;
    engine.onTime = (t) => opts.onProgress?.({ phase: 'video', progress: 0.05 + 0.9 * (t / Math.max(0.001, durationSec)) });
    const done = new Promise<void>((resolve) => {
        engine.onPlayingChange = (playing) => { if (!playing) resolve(); };
    });
    const onAbort = () => engine.pause();
    signal?.addEventListener('abort', onAbort);
    engine.play();
    await done;
    signal?.removeEventListener('abort', onAbort);
    await new Promise((r) => setTimeout(r, 200));
    recorder.stop();
    await stopped;
    audioNode?.stop();
    void audioCtx?.close();
    engine.dispose();
    stream.getTracks().forEach((tr) => tr.stop());
    checkAbort(signal);
    const type = (mime || 'video/webm').split(';')[0];
    opts.onProgress?.({ phase: 'finalize', progress: 1 });
    return {
        blob: new Blob(chunks, { type }),
        mimeType: type,
        extension,
        width,
        height,
        durationSec,
        thumbnail,
        method: 'mediarecorder',
        hasAudio: !!audioBuffer,
    };
}

// ─── entry ────────────────────────────────────────────────────────────

export async function exportComposition(input: VideoComposition, opts: ExportOptions = {}): Promise<ExportResult> {
    const comp = normalizeComposition(input);
    if (totalDuration(comp) <= 0) throw new Error('Nothing to export — the timeline is empty');
    opts.onProgress?.({ phase: 'prepare', progress: 0 });
    await ensureFonts(fontsUsed(comp));
    invalidateTextLayouts();
    checkAbort(opts.signal);
    const media = new MediaCache({ posterFallback: false, withSound: false });
    try {
        await media.whenReady(comp);
        checkAbort(opts.signal);
        if (!opts.forceRealtime && hasWebCodecs()) {
            try {
                return await exportWebCodecs(comp, opts, media);
            } catch (err: any) {
                if (err?.name === 'AbortError') throw err;
                // An encoder that refuses mid-way still leaves the real-time path.
                // eslint-disable-next-line no-console
                console.warn('[video-editor] WebCodecs export failed, recording in real time instead', err);
            }
        }
        return await exportRealtime(comp, opts, media);
    } finally {
        media.dispose();
    }
}
